import {
  Bell,
  goToKeys,
  parseKey,
  pinCommand,
  placeCounts,
  railCommand,
  SearchButton,
  ShortcutKeys,
  shortcutKeys,
  sumOf,
  toggleSection,
  useClosedSections,
  usePins,
  useRail,
} from "@nexxt/app-kit";
import {
  Button,
  cn,
  LogoMark,
  Sidebar,
  SidebarItem,
  SidebarPlaces,
  SidebarSection,
  Tooltip,
  TooltipGroup,
  YourTurnBadge,
} from "@nexxt/ui";
import { Link, type LinkOptions, linkOptions, useNavigate } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import { ArrowLeft, PanelLeftClose, PanelLeftOpen, PinOff, Settings } from "lucide-react";
import type { ComponentProps, MouseEvent } from "react";
import { m } from "../../paraglide/messages.js";
import { bellClient } from "../bell-client";
import { type Queue, type Todo, useQueues } from "../features/home/queues";
import { followClient } from "../follow-client";
import { type Area, type NavItem, sectionsOf } from "./navigation";
import { UserMenu } from "./user-menu";

// The sidebar of Backstage (ADR-114): Now and the pinned places on top, then the sections of the area. The app area
// ends with Admin, which opens the admin area, with the way back. Every place carries the sum of the home's "your
// turn" queues that open it. The same content in the column (from lg, wide or as icons) and in the sheet (below).

interface AppSidebarProps {
  /** The column from lg, which can be narrow, or the sheet of the menu below lg, always wide. */
  variant: "column" | "sheet";
  area: Area;
  places: NavItem[];
  /** The place on display, which carries the indicator. */
  current: NavItem | undefined;
  /** Where "Back to Backstage" goes: the last page of the app area. */
  backTo: string;
  onSearch: () => void;
  onShortcuts: () => void;
  /** In the sheet: a link followed closes it, even towards the page on display. */
  onFollowLink?: () => void;
}

export function AppSidebar(props: AppSidebarProps) {
  const { area, places, current } = props;
  const narrow = useRail();
  const rail = props.variant === "column" && narrow;
  const closed = useClosedSections();
  const { yourTurn, waiting, todo } = useQueues();
  // a place with the filter "To do" counts each row once, as the filter does (ADR-302); the sum of its queues while
  // the union has no count
  const counts = {
    ...placeCounts(yourTurn.map((q) => ({ place: q.place, count: q.data?.count ?? 0 }))),
    ...Object.fromEntries(
      Object.entries(todo).flatMap(([place, { count }]) => (count === undefined ? [] : [[place, count]])),
    ),
  };
  const pinned = pinnedItems(usePins(), places, yourTurn, waiting, counts, todo);
  const adminPlaces = sectionsOf(places, "admin").flatMap((s) => s.places);
  const now = places.find((place) => place.id === "now");
  const season = places.find((place) => place.id === "season-calendar");
  const blockers = places.find((place) => place.id === "season-blockers");
  const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
  const follow = props.onFollowLink;

  return (
    <Sidebar
      label={m.nav_label()}
      rail={rail}
      className={
        props.variant === "column"
          ? cn(
              "shrink-0 border-r border-border bg-muted py-3 transition-[width] duration-150 ease-out",
              rail ? "w-14 items-center px-2" : "w-60 px-3",
            )
          : "flex-1 p-3"
      }
      {...(follow && {
        onClick: (e: MouseEvent) => {
          if (e.target instanceof Element && e.target.closest("a")) follow();
        },
      })}
    >
      <TooltipGroup>
        <div className={cn("flex items-center gap-1", rail ? "flex-col" : "pr-0.5")}>
          <LogoMark
            product="backstage"
            className={cn("py-2", rail ? "px-0" : "flex-1 px-2")}
            {...(rail && { nameClassName: "hidden", productClassName: "hidden" })}
          />
          {props.variant === "column" && <RailToggle rail={rail} />}
        </div>
        <SearchButton onClick={props.onSearch} className="mt-1" />
        <SidebarPlaces className="-mx-1 mt-2 px-1 pb-2 [scrollbar-width:thin]">
          {area === "admin" ? (
            <SidebarItem
              render={<BackLink href={props.backTo} />}
              icon={<ArrowLeft aria-hidden />}
              label={m.nav_back_to_app()}
            />
          ) : (
            <>
              {now && <Place place={now} active={current === now} count={total} />}
              {season && <Place place={season} active={current === season} count={0} />}
              {blockers && <Place place={blockers} active={current === blockers} count={0} />}
            </>
          )}
          {area === "app" && pinned.length > 0 && (
            <SidebarSection
              label={m.nav_pinned()}
              open={!closed.includes("pinned")}
              onOpenChange={() => toggleSection("pinned")}
            >
              {pinned.map((item) => (
                <SidebarItem
                  key={item.key}
                  render={<Link {...item.link} />}
                  icon={<item.icon aria-hidden />}
                  label={item.label}
                  count={item.count}
                  countLabel={m.your_turn_sr()}
                  action={<UnpinButton pinKey={item.key} name={item.label} />}
                />
              ))}
            </SidebarSection>
          )}
          {sectionsOf(places, area).map(({ section, places: inSection }) => (
            <SidebarSection
              key={section.id}
              label={section.label()}
              open={!closed.includes(section.id)}
              onOpenChange={() => toggleSection(section.id)}
              badge={<YourTurnBadge count={sumOf(inSection, counts)} label={m.your_turn_sr()} />}
            >
              {inSection.map((place) => (
                <Place
                  key={place.id}
                  place={place}
                  active={current === place}
                  count={counts[place.id] ?? 0}
                  todo={todo[place.id]?.link}
                />
              ))}
            </SidebarSection>
          ))}
        </SidebarPlaces>
        <div className="flex flex-col gap-1 border-t border-border pt-2">
          {area === "app" && adminPlaces[0] && (
            <SidebarItem
              render={<Link to={adminPlaces[0].to} />}
              icon={<Settings aria-hidden />}
              label={m.nav_admin()}
              count={sumOf(adminPlaces, counts)}
              countLabel={m.your_turn_sr()}
            />
          )}
          {/* below `lg` the bell is in the top bar: a sheet opened from the menu would close with it */}
          {props.variant === "column" && <Bell client={bellClient} follows={followClient} variant="item" />}
          <UserMenu compact={rail} onShortcuts={props.onShortcuts} />
        </div>
      </TooltipGroup>
    </Sidebar>
  );
}

/** A place or a queue of the home the user pinned, with what it opens and its count. */
interface PinnedItem {
  key: string;
  label: string;
  icon: LucideIcon;
  link: LinkOptions;
  /** "Your turn" count; zero for the queues that wait on others. */
  count: number;
}

/** The pins the user can still open: a place no longer permitted, or a queue no longer theirs, is left out. */
function pinnedItems(
  pins: string[],
  places: NavItem[],
  yourTurn: Queue[],
  waiting: Queue[],
  counts: Record<string, number>,
  todo: Record<string, Todo>,
): PinnedItem[] {
  return pins.flatMap((key): PinnedItem[] => {
    const pin = parseKey(key);
    if (pin?.kind === "place") {
      const place = places.find((p) => p.id === pin.id);
      if (!place) return [];
      const count = counts[place.id] ?? 0;
      const todoLink = count > 0 && todo[place.id]?.link;
      return [
        {
          key,
          label: place.label(),
          icon: place.icon,
          link: todoLink || linkOptions({ to: place.to }),
          count,
        },
      ];
    }
    const mine = yourTurn.find((q) => q.id === pin?.id);
    const queue = mine ?? waiting.find((q) => q.id === pin?.id);
    const icon = places.find((p) => p.id === queue?.place)?.icon;
    if (!queue || !icon) return [];
    return [{ key, label: queue.title, icon, link: queue.link, count: mine ? (queue.data?.count ?? 0) : 0 }];
  });
}

/** With something to do, a place with the filter "To do" opens its list with it on (ADR-302). */
function Place({
  place,
  active,
  count,
  todo,
}: {
  place: NavItem;
  active: boolean;
  count: number;
  todo?: LinkOptions | undefined;
}) {
  return (
    <SidebarItem
      render={<Link {...(todo && count > 0 ? todo : { to: place.to })} />}
      icon={<place.icon aria-hidden />}
      label={place.label()}
      active={active}
      count={count}
      countLabel={m.your_turn_sr()}
      {...(place.key && { shortcut: <ShortcutKeys keys={goToKeys(place.key)} /> })}
    />
  );
}

/** Back to the last page of the app area, filters included; a click with a modifier still opens a new tab. */
function BackLink({ href, ...props }: ComponentProps<"a"> & { href: string }) {
  const navigate = useNavigate();
  return (
    <a
      href={href}
      {...props}
      onClick={(e) => {
        props.onClick?.(e);
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        void navigate({ href });
      }}
    />
  );
}

function RailToggle({ rail }: { rail: boolean }) {
  const command = railCommand(rail);
  return (
    <Tooltip
      content={
        <>
          {command.label}
          <ShortcutKeys keys={shortcutKeys.rail} />
        </>
      }
      side={rail ? "right" : "bottom"}
    >
      <Button
        variant="ghost"
        size="icon"
        aria-label={command.label}
        className="text-muted-foreground"
        onClick={command.run}
      >
        {rail ? <PanelLeftOpen aria-hidden /> : <PanelLeftClose aria-hidden />}
      </Button>
    </Tooltip>
  );
}

function UnpinButton({ pinKey, name }: { pinKey: string; name: string }) {
  const unpin = pinCommand(pinKey, name, true);
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={unpin.label}
      className="size-6 pointer-coarse:size-(--control-h) [&_svg]:size-3.5"
      onClick={unpin.run}
    >
      <PinOff aria-hidden />
    </Button>
  );
}
